import toastr from 'toastr';

export const CaseGroup = {
  namespaced: true,

  state: {
    groups: [],
    loading: false,
    currentGroup: null,
    currentGroupUsers: [],
    currentUserStatus: {
      isCreator: false,
      isOwner: false,
      isAdmin: false,
      isMember: false,
    },
    groupNotificationsControl: {
      publish_case: { mobileFlag: 1, emailFlag: 1, mobileAvailable: 0, emailAvailable: 1 },
    },
  },

  getters: {
    getCurrentGroup: (state) => {
      return state.currentGroup;
    },

    getCurrentGroupUsers(state) {
      return state.currentGroupUsers;
    },

    groupById: (state) => (id) => {
      return state.groups.filter((group) => {
        return group.id === id;
      })[0];
    },

    areGroupsLoaded: (state) => {
      return state.groups.length;
    },

    isCurrentGroupLoaded: (state) => {
      return state.currentGroup !== null;
    },

    getGroups: (state) => () => {
      return state.groups ?? [];
    },

    getGroup: (state) => () => {
      return state.group;
    },

    groupNotificationsControlGet(state) {
      return state.groupNotificationsControl;
    },
  },

  mutations: {
    setBusy(state, loading) {
      state.loading = loading;
    },

    setGroup(state, { group }) {
      state.group = group;
    },

    setCurrentUserStatus(state, user) {
      state.currentUserStatus = user;
    },

    setGroups(state, groups) {
      groups = groups.concat(state.groups);
      let uniqueGroups = [],
        ids = [];
      for (let i = 0; i < groups.length; i++) {
        if (!ids.includes(groups[i].id)) {
          ids.push(groups[i].id);
          uniqueGroups.push(groups[i]);
        }
      }
      state.groups = uniqueGroups;
    },

    setCurrentGroup(state, group) {
      state.currentGroup = group;
    },

    setCurrentGroupUsers(state, users) {
      state.currentGroupUsers = users;
    },

    addGroup(state, group) {
      state.groups.push(group);
    },

    updateGroup(state, group) {
      commit('setCurrentGroupUsers', [...group.owners, ...group.members]);
      state.groups = state.groups.filter((currentGroup) => {
        return currentGroup.id !== group.id;
      });
      state.groups.push(group);
    },

    groupSetNotificationsControl(state, notificationsControl) {
      for (const key of Object.keys(notificationsControl)) {
        state['groupNotificationsControl'][key] = {
          ...state['groupNotificationsControl'][key],
          ...notificationsControl[key],
        };
      }
    },
  },

  actions: {
    async loadGroup({ commit, dispatch }, { slug }) {
      if (!slug) {
        console.warn('Slug is required to load the group.');
        throw new Error('Invalid slug');
      }

      commit('setBusy', true);

      try {
        const response = await this._vm.$api('groups.show', { version: '1.0', group: slug });

        if (!response || !response.data || !response.data.data) {
          throw new Error('Invalid response structure from API');
        }

        const currentGroup = response.data.data;

        commit('setCurrentGroup', currentGroup);
        commit('setCurrentGroupUsers', [...currentGroup.owners, ...currentGroup.members]);
        commit('groupSetNotificationsControl', currentGroup.notificationsControl);
        this.dispatch('casesFiltersChangeGroupId', currentGroup.id);
      } catch (error) {
        console.error('Error in loadGroup:', error.message);
        throw error;
      } finally {
        commit('setBusy', false);
      }
    },

    async setGroups({ commit }) {
      try {
        const response = await this._vm.$api('groups.index', { version: '1.0' });

        let groups = response.data.data;
        commit('setGroups', groups);
      } catch (err) {
        console.error(err);
      }
    },

    requestCurrentGroup({ commit }, slug) {
      this._vm
        .$api('group.get', ['1.0', slug])
        .then((response) => {
          let currentGroup = response.data.data;
          commit('setCurrentGroup', currentGroup);
          commit('addGroup', currentGroup);
          commit('setCurrentGroupUsers', [...currentGroup.owners, ...currentGroup.members]);
          commit('groupSetNotificationsControl', currentGroup.notificationsControl);
        })
        .catch((error) => {
          window.location.href = this._vm.$route('home').url();
        })
        .finally();
    },

    saveNotificationsControlGroup({ state, commit }, notificationsControl) {
      commit('setBusy', true);
      let groupId = state.currentGroup.id;
      this._vm
        .$api('group.save.notifications.control', ['1.0', groupId], notificationsControl)
        .then((response) => {
          commit('groupSetNotificationsControl', response.data.data.notificationsControl);
          commit('setBusy', false);
          toastr.success('Saved!');
        })
        .catch(() => {
          toastr.error('Some error occurred!');
        })
        .finally();
    },

    setUserStatus({ state, commit }, user) {
      const userStatus = {
        isCreator: user.id == state.currentGroup?.creator.id,
        isOwner: state.currentGroup?.owners.some((e) => e.id == user.id),
        isMember: state.currentGroup?.members.some((e) => e.id == user.id),
      };
      commit('setCurrentUserStatus', userStatus);
    },
  },
};
